/**
 * AI Support Assistant API Client
 *
 * Covers conversations, messages, escalation, ratings, and admin metrics.
 * Routes: /api/assistant/*
 *
 * The `ss-api` service that backs these routes may be disabled in nginx
 * (returns 503 `feature_disabled`). Callers should catch
 * `FeatureDisabledError` from `./_http` and degrade to an empty state.
 */

import { apiFetch } from './_http';

const BASE = '/api/assistant';

// ── Types ────────────────────────────────────────────────────────────────────

export type ConversationStatus = 'active' | 'escalated' | 'resolved' | 'closed';

export interface Conversation {
  id: string;
  user_id: string;
  status: ConversationStatus;
  intent: string | null;
  confidence: number | null;
  escalation_level: number;
  kb_citations: string[];
  created_at: string;
  updated_at: string;
  last_message_at: string | null;
  message_count?: number;
}

export interface Message {
  id: string;
  conversation_id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  rating: 'helpful' | 'neutral' | 'unhelpful' | null;
  created_at: string;
}

export interface AdminMetrics {
  total_conversations: number;
  active_conversations: number;
  escalated_conversations: number;
  avg_messages_per_conversation: number;
  avg_confidence: number;
  resolution_rate: number;
}

// ── API ──────────────────────────────────────────────────────────────────────

export const assistantApi = {
  async listConversations(params?: {
    status?: ConversationStatus;
    cursor?: string;
    limit?: number;
  }): Promise<{ conversations: Conversation[]; next_cursor: string | null }> {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.cursor) qs.set('cursor', params.cursor);
    if (params?.limit) qs.set('limit', String(params.limit));
    const query = qs.toString() ? `?${qs}` : '';
    return apiFetch(`${BASE}/conversations${query}`);
  },

  async getConversation(id: string): Promise<Conversation> {
    return apiFetch(`${BASE}/conversations/${id}`);
  },

  async getMessages(conversationId: string): Promise<{ messages: Message[] }> {
    return apiFetch(`${BASE}/conversations/${conversationId}/messages`);
  },

  async sendMessage(conversationId: string, content: string): Promise<Message> {
    return apiFetch(`${BASE}/conversations/${conversationId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    });
  },

  async escalate(conversationId: string, reason?: string): Promise<Conversation> {
    return apiFetch(`${BASE}/conversations/${conversationId}/escalate`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  },

  async rateMessage(conversationId: string, messageId: string, rating: 'helpful' | 'neutral' | 'unhelpful'): Promise<void> {
    return apiFetch(`${BASE}/conversations/${conversationId}/messages/${messageId}/rate`, {
      method: 'POST',
      body: JSON.stringify({ rating }),
    });
  },

  async getAdminMetrics(): Promise<AdminMetrics> {
    return apiFetch(`${BASE}/admin/metrics`);
  },
};
